/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms/views/View'
import { BoStack } from './BoStack'
import { useBoSizeClass } from './useBoSizeClass'

interface PropsType {
	/** 페이지의 바깥 래퍼 클래스 — 데스크톱·태블릿은 이 클래스의 View 하나에 좌·우가 그대로 든다(DOM 불변) */
	className?: string
	/** 좌 목록 판 */
	list: React.ReactNode
	/** 우 자세히 판 */
	detail: React.ReactNode
	/** 고른 항목의 키. '' 이면 아무것도 안 고른 것 — 폰에서 스택을 닫는다 */
	detailKey: string
	/** 폰 스택 상단 바 제목 */
	title: string
}

/**
 * 좌우 분할 페이지(제품 · 시즌 · 납품가) — RESPONSIVE_PLAN §6.4.
 *   데스크톱·태블릿 = 지금 그대로 두 판(페이지 css 가 폭·스택을 정한다).
 *   폰 = 목록 한 판 → 항목을 고르면 우 판 내용이 **한 depth 들어간 전폭 스택**(BoStack)으로 열린다. ‹ 로 돌아온다.
 * 열림은 두 신호로 잡는다 — 키가 바뀌었을 때, 그리고 목록에서 항목을 눌렀을 때(같은 항목 다시 누르기).
 * 페이지는 등급을 모른다 — 여기가 유일한 분기다.
 */
export const BoSplit = (props: PropsType) => {
	const sizeClass = useBoSizeClass()
	const [open, setOpen] = React.useState<boolean>(false)
	const [pending, setPending] = React.useState<boolean>(false)
	const prevKey = React.useRef<string>(props.detailKey)

	// 키가 바뀌면(마운트 이후) 연다. 비면 닫는다
	React.useEffect(() => {
		if (props.detailKey === '') setOpen(false)
		else if (props.detailKey !== prevKey.current) setOpen(true)
		prevKey.current = props.detailKey
	}, [props.detailKey])

	// 목록 안 항목을 눌렀다 — 렌더가 지나간 뒤 키가 있으면 연다(같은 항목을 다시 골라도 열린다)
	React.useEffect(() => {
		if (!pending) return
		setPending(false)
		if (props.detailKey !== '') setOpen(true)
	}, [pending, props.detailKey])

	if (sizeClass !== 'phone') {
		return (
			<View className={props.className}>
				{props.list}
				{props.detail}
			</View>
		)
	}

	const onListClick = (e: React.MouseEvent) => {
		const target = e.target as HTMLElement
		// 헤더의 버튼(등록 · 필터)은 항목 선택이 아니다
		if (target.closest('.boBoxHeader')) return
		if (target.closest('button, [role="button"], li, tr, a')) setPending(true)
	}

	return (
		<View className={`${props.className ?? ''} boSplit phone`}>
			<View className="boSplitList" onClick={onListClick}>
				{props.list}
			</View>
			<BoStack open={open} onClose={() => setOpen(false)} title={props.title}>
				<View css={detailStyle} className="boSplitDetail">
					{props.detail}
				</View>
			</BoStack>
		</View>
	)
}

/**
 * 스택 안의 우 판 — BoxView 는 `height: 100%` + 본문 `overflow: hidden`(폰) 이라 스택 스크롤러 안에서 위가 잘렸다.
 * 스택 본문이 유일한 스크롤러다 — 박스는 내용 높이만큼, 본문은 자르지 않는다.
 */
const detailStyle = css({
	'.boBox': { height: 'auto', minHeight: 0, padding: 0 },
	'.boBox .boxChildrenView': { flex: 'none', overflow: 'visible', paddingBottom: 24 },
})
